import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  linkPlugin,
  linkDialogPlugin,
  toolbarPlugin,
  BoldItalicUnderlineToggles,
  ListsToggle,
  BlockTypeSelect,
  CreateLink,
  type MDXEditorMethods,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { Organizer } from '../../utils/types';
import { useUpdateOrganizer } from '../../api/organizerAdmin';
import './CampEditModal.css';

interface OrganizerInfoEditModalProps {
  organizer: Organizer;
  isOpen: boolean;
  onClose: () => void;
}

const OrganizerInfoEditModal: React.FC<OrganizerInfoEditModalProps> = ({ organizer, isOpen, onClose }) => {
  const [name, setName] = useState(organizer.name);
  const [description, setDescription] = useState(organizer.description || '');
  const [error, setError] = useState<string | null>(null);

  const descriptionRef = useRef<MDXEditorMethods>(null);
  const updateOrganizer = useUpdateOrganizer(String(organizer.id));

  useEffect(() => {
    setName(organizer.name);
    setDescription(organizer.description || '');
    setError(null);
    descriptionRef.current?.setMarkdown(organizer.description || '');
  }, [organizer]);

  if (!isOpen) return null;

  const editorPlugins = [
    headingsPlugin(),
    listsPlugin(),
    linkPlugin(),
    linkDialogPlugin(),
    toolbarPlugin({
      toolbarContents: () => (
        <>
          <BlockTypeSelect />
          <BoldItalicUnderlineToggles />
          <ListsToggle />
          <CreateLink />
        </>
      ),
    }),
  ];

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Organizer name is required.');
      return;
    }
    setError(null);
    try {
      await updateOrganizer.mutateAsync({ name: name.trim(), description: description || null });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update organizer.');
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="camp-edit-modal">
        <button className="modal-close-btn" onClick={onClose} disabled={updateOrganizer.isPending}>
          <X />
        </button>

        <h2 className="camp-edit-modal__title">Edit Organizer</h2>

        {error && <div className="message message--error">{error}</div>}

        <div className="camp-edit-modal__form">
          <div className="form-group">
            <label htmlFor="organizer-name">Name</label>
            <input
              id="organizer-name"
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={updateOrganizer.isPending}
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <div className="camp-edit-modal__editor-wrapper">
              <MDXEditor
                ref={descriptionRef}
                markdown={description}
                onChange={setDescription}
                plugins={editorPlugins}
                readOnly={updateOrganizer.isPending}
              />
            </div>
          </div>
        </div>

        <div className="camp-edit-modal__actions">
          <button className="btn btn--secondary btn--md" onClick={onClose} disabled={updateOrganizer.isPending}>
            Cancel
          </button>
          <button className="btn btn--primary btn--md" onClick={handleSave} disabled={updateOrganizer.isPending}>
            {updateOrganizer.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrganizerInfoEditModal;
